import React, { forwardRef, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from '@phosphor-icons/react';
import { cn } from '../../../lib/cn';
import { Icon } from '../../Icon';
import { mergeRefs, useModalLayer } from './focus';

/**
 * Modal, the desktop replacement for the mobile bottom sheet on FORMS.
 *
 * CSS: `desktop/system.css` `.dsk-modal-scrim`, `.dsk-modal`, plus
 * `.dsk-card__header` for the title row, `.dsk-panel__title` for the heading
 * and `.dsk-field__foot` for the action row.
 * Behaviour: no numbered reference behaviour. This is a RE-SKIN of the app's
 * existing `components/UI/Modal/Modal.jsx`, which was already good, so every
 * accessibility property it had is preserved and none is regressed:
 *
 *   portal to <body>  ·  focus saved on open and RESTORED on close  ·  first
 *   focusable focused on open  ·  Tab trapped inside the dialog  ·  Escape
 *   closes  ·  scrim click closes on mousedown (so a drag that starts inside
 *   the dialog and ends on the scrim does not close it)  ·  body scroll lock  ·
 *   role="dialog" + aria-modal + a labelled title.
 *
 * Two things the app version did that are kept verbatim because they were bug
 * fixes: the close handler is read from a ref so an inline arrow does not
 * retrigger the focus effect (it used to ping-pong focus), and the first-focus
 * call is deferred a tick so the dialog has layout to focus into. Both now live
 * in `useModalLayer`, shared with Drawer.
 *
 * One thing ADDED: the scroll lock pads the scrollbar gap back, so the page no
 * longer jumps left when the modal opens.
 *
 * SIZE. The layer defines exactly one modal width,
 * `width: min(560px, calc(100vw - 48px))`. There is no `sm`/`lg`/`full`
 * modifier in `desktop/system.css`, so this component deliberately exposes no
 * `size` prop rather than inventing classes that do not exist. Anything that
 * genuinely needs more room is a Drawer or a page.
 */
export const Modal = forwardRef(function Modal(
  {
    open,
    onClose,
    title,
    children,
    footer,
    closeOnEsc = true,
    closeOnScrim = true,
    showClose = true,
    ariaLabel = 'Dialog',
    initialFocusRef,
    scrimClassName,
    className,
    ...rest
  },
  ref,
) {
  const panelRef = useRef(null);
  const titleId = useId();

  useModalLayer({
    active: Boolean(open),
    panelRef,
    onEscape: closeOnEsc ? onClose : undefined,
    initialFocusRef,
  });

  if (!open || typeof document === 'undefined') return null;

  const onScrimMouseDown = (event) => {
    if (!closeOnScrim) return;
    if (event.target === event.currentTarget && onClose) onClose();
  };

  return createPortal(
    <div
      className={cn('dsk-modal-scrim', scrimClassName)}
      role="presentation"
      onMouseDown={onScrimMouseDown}
    >
      <div
        ref={mergeRefs(panelRef, ref)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        tabIndex={-1}
        className={cn('dsk-modal', className)}
        {...rest}
      >
        {title || showClose ? (
          <header className="dsk-card__header">
            {title ? (
              <h2 id={titleId} className="dsk-panel__title">
                {title}
              </h2>
            ) : (
              <span />
            )}
            {showClose ? (
              <button type="button" className="dsk-iconbtn" aria-label="Close" onClick={onClose}>
                <Icon as={X} />
              </button>
            ) : null}
          </header>
        ) : null}

        {children}

        {footer ? <footer className="dsk-field__foot">{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
});

export default Modal;
